import { APPLIED_FILTER_REMOVE_ATTR } from '../../hooks/useAppliedFilterRemoveFocus';
import { cn } from '../../styles/lib/utils';
import type { FilterGroupItem, FilterLineItem, FilterType } from '../../utils/filterSettings';
import { getFilterOperatorTypes, NULL_OPERATOR_TYPES, OPERATOR_TYPES } from '../../utils/filterSettings';
import { Badge } from '../Badge';
import { IconButton } from '../Button';
import { Text } from '../Text';

export type AppliedFilterProps = {
  filter: FilterLineItem | FilterGroupItem;
  onClose?: () => void;
  className?: string;
  /**
   * Hide the column name in the filter display
   * Useful when the filter is shown in a column-specific context (e.g., ColumnMenu)
   */
  hideColumnName?: boolean;
  /**
   * Array of filter type definitions used to get human-readable labels
   * for filter types and operators
   */
  filterTypes?: FilterType[];
  /**
   * Matched filter type used to get the filter value config and operator type config, provided from column meta manually
   */
  matchedFilterType?: FilterType;
};

/**
 * Format the filter value for display based on its type
 * Uses the filterValueConfig from the FilterType meta information
 */
function formatFilterValue(filter: FilterLineItem, matchedFilterType: FilterType): string {
  const valueConfig = matchedFilterType.filterValueConfig;

  // Format boolean values
  if (valueConfig?.type === 'boolean') {
    return filter.value === 'true' ? 'True' : 'False';
  }

  const value = filter.value as string;
  if (!value) return '';

  // Format date values
  if (valueConfig?.type === 'date') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    }
  }

  // Format select values - use label from options if available
  if (valueConfig?.type === 'select') {
    const option = valueConfig.options.find((opt) => opt.value === value);
    return option?.label || value;
  }

  // For text and nested types, return value as-is
  return value;
}

/**
 * Count total number of filters in a group (recursive)
 */
function countFilters(group: FilterGroupItem): number {
  let count = 0;
  for (const item of group.items) {
    if (item.type === 'line') {
      count++;
    } else if (item.type === 'group') {
      count += countFilters(item);
    }
  }
  return count;
}

/**
 * Format a filter label by finding the matching FilterType in filterTypes array
 * Returns the human-readable label from filterTypes, or falls back to filter.filterType?.label
 */
function formatFilterLabel(filter: FilterLineItem, matchedFilterType: FilterType): string {
  return matchedFilterType?.label || filter.filterType?.label || '';
}

const FALLBACK_OPERATOR_TYPES = [...OPERATOR_TYPES.TEXT, ...NULL_OPERATOR_TYPES];

/**
 * Format a filter operator label by finding the matching operator in the FilterType's filterOperatorTypeConfig
 * Empty-check labels apply to any column because a filter read back from the URL keeps the raw operator id as its label
 */
function formatFilterOperator(filter: FilterLineItem, matchedFilterType: FilterType): string {
  // Get operator label from the matched FilterType's filterOperatorTypeConfig if available
  if (matchedFilterType && filter.operatorType?.id) {
    const matchedOperator = getFilterOperatorTypes(matchedFilterType).find((ot) => ot.id === filter.operatorType?.id);
    if (matchedOperator?.label) {
      return matchedOperator.label;
    }
  }

  const fallbackOperator = FALLBACK_OPERATOR_TYPES.find((ot) => ot.id === filter.operatorType?.id);
  return fallbackOperator?.label || filter.operatorType?.label || '';
}

const resolveFilterType = (filter: FilterLineItem, filterTypes: FilterType[], matchedFilterType?: FilterType) =>
  matchedFilterType ?? filterTypes.find((ft) => ft.id === filter.filterType?.id) ?? filter.filterType;

/** The lines of an `or` group of 2+ `eq` lines on one column that opts into `combineOrGroups`, otherwise null. */
function getCombinedOrLines(
  group: FilterGroupItem,
  filterTypes: FilterType[],
  matchedFilterType?: FilterType,
): FilterLineItem[] | null {
  if (group.operator !== 'or' || group.items.length < 2) return null;
  const lines = group.items.filter((item): item is FilterLineItem => item.type === 'line');
  if (lines.length !== group.items.length) return null;
  const column = lines[0].filterType?.id;
  const sameColumnEq = lines.every((line) => line.filterType?.id === column && line.operatorType?.id === 'eq');
  return sameColumnEq && resolveFilterType(lines[0], filterTypes, matchedFilterType)?.combineOrGroups ? lines : null;
}

/**
 * Format a filter line item into a user-friendly display string
 */
type FormatFilterLineProps = {
  filter: FilterLineItem;
  filterTypes: FilterType[];
  matchedFilterType?: FilterType;
  hideColumnName: boolean;
};
function getFilterLineParts({ filter, filterTypes, matchedFilterType }: Omit<FormatFilterLineProps, 'hideColumnName'>) {
  const resolvedMatchedFilterType = resolveFilterType(filter, filterTypes, matchedFilterType);

  if (!resolvedMatchedFilterType) {
    return null;
  }

  return {
    filterTypeLabel: formatFilterLabel(filter, resolvedMatchedFilterType),
    operatorTypeLabel: formatFilterOperator(filter, resolvedMatchedFilterType),
    value: formatFilterValue(filter, resolvedMatchedFilterType),
  };
}

type FilterLineParts = NonNullable<ReturnType<typeof getFilterLineParts>>;

function combineLineParts(parts: ReturnType<typeof getFilterLineParts>[]): FilterLineParts | null {
  const [first] = parts;
  if (!first || parts.some((part) => !part)) return null;
  return { ...first, value: parts.map((part) => part?.value).join(' or ') };
}

function renderFilterLineParts(
  { filterTypeLabel, operatorTypeLabel, value }: FilterLineParts,
  hideColumnName: boolean,
): React.ReactNode {
  return (
    <>
      {!hideColumnName && (
        <Text className="text-xs text-blue-60 font-semibold whitespace-nowrap">{filterTypeLabel}</Text>
      )}
      <Text className="text-xs text-blue-60 font-normal whitespace-nowrap">{operatorTypeLabel.toLowerCase()}</Text>
      <Text className="text-xs text-blue-60 font-semibold whitespace-nowrap min-w-0 flex-1 truncate">{value}</Text>
    </>
  );
}

/**
 * Applied Filter component that displays an active filter in a pill format
 * Can represent either a filter line (e.g., "Status: not Active") or a filter group (e.g., "Filter group: 9 filters")
 */
function AppliedFilter({
  filter,
  onClose,
  className,
  hideColumnName = false,
  filterTypes = [],
  matchedFilterType,
}: AppliedFilterProps) {
  const lines = filter.type === 'line' ? [filter] : getCombinedOrLines(filter, filterTypes, matchedFilterType);
  const isGroup = !lines;
  const filterCount = filter.type === 'group' ? countFilters(filter) : 0;
  const lineParts =
    lines &&
    combineLineParts(lines.map((line) => getFilterLineParts({ filter: line, filterTypes, matchedFilterType })));
  // Names the column even when hideColumnName drops it visually, so each remove button is distinct.
  const removeLabel = isGroup
    ? 'Remove filter group'
    : ['Remove filter', lineParts?.filterTypeLabel, lineParts?.operatorTypeLabel.toLowerCase(), lineParts?.value]
        .filter(Boolean)
        .join(' ');

  return (
    <Badge
      asChild
      color="blue"
      kind="outline"
      className={cn('flex gap-0 pl-2 pr-1 rounded-lg text-blue-60 overflow-hidden', className)}
    >
      <div>
        <div className="flex items-center gap-0.5 leading-normal cursor-default min-w-0 flex-1 overflow-hidden">
          {isGroup ? (
            <>
              <Text className="text-xs text-blue-60 font-semibold">Filter group:</Text>
              <Text className="text-xs text-blue-60 font-semibold">
                {filterCount} {filterCount === 1 ? 'filter' : 'filters'}
              </Text>
            </>
          ) : (
            lineParts && renderFilterLineParts(lineParts, hideColumnName)
          )}
        </div>

        {onClose && (
          <IconButton
            variant="hollow"
            icon="crossCircled"
            size="icon"
            iconSize={12}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="rounded-full hover:opacity-70 hover:bg-transparent transition-opacity text-blue-60 cursor-pointer flex-none"
            aria-label={removeLabel}
            {...{ [APPLIED_FILTER_REMOVE_ATTR]: true }}
          />
        )}
      </div>
    </Badge>
  );
}

export { AppliedFilter };
